'use client';

import { type ReactNode } from 'react';
import { Drawer as VaulDrawer } from 'vaul';

import { useIsMobile } from '@ui/hooks/useIsMobile';
import { cn } from '@ui/lib/utils';

import { Dialog } from '../basicDialog';

export interface ResponsiveDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;
  /**
   * Callback when the dialog should be closed
   */
  onClose: () => void;
  /**
   * Dialog title (optional)
   */
  title?: string;
  /**
   * Dialog description (optional)
   */
  description?: string;
  /**
   * Dialog content
   */
  children: ReactNode;
  /**
   * Additional CSS classes for the dialog content
   */
  className?: string;
  /**
   * Size variant of the dialog (only applies to desktop)
   * @default "default"
   */
  size?: 'sm' | 'default' | 'lg' | 'xl';
  /**
   * Whether to show the close button
   * @default true
   */
  showCloseButton?: boolean;
  /**
   * Sheet side for mobile (only applies to mobile)
   * @default "bottom"
   */
  mobileSide?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * When true on mobile, removes the sheet's horizontal + bottom padding so
   * content can scroll edge-to-edge (scrollbar aligned with the drawer edge,
   * no white gap at the bottom). The consumer is responsible for adding
   * inner horizontal padding on its own scroll area.
   * @default false
   */
  mobileFullBleed?: boolean;
  /**
   * Mobile (Vaul) only. When false, disables Vaul's automatic "lift the whole
   * sheet above the on-screen keyboard" behaviour. Drawers that already have
   * their own internal scroll area should set this false: otherwise Vaul's
   * translate double-compensates and lifts the sheet far above the keyboard,
   * revealing the background. With it off, the focused input is scrolled into
   * view within the sheet's own scroll container instead.
   * @default true
   */
  repositionInputs?: boolean;
}

const MOBILE_DIRECTION_CLASSES: Record<'top' | 'right' | 'bottom' | 'left', string> = {
  bottom: 'inset-x-0 bottom-0 mt-24 max-h-[95vh] rounded-t-xl border-t',
  top: 'inset-x-0 top-0 mb-24 max-h-[95vh] rounded-b-xl border-b',
  right: 'inset-y-0 right-0 w-3/4 max-w-sm border-l',
  left: 'inset-y-0 left-0 w-3/4 max-w-sm border-r'
};

/**
 * A responsive dialog component that shows as a dialog on desktop and a
 * draggable drawer (Vaul) on mobile. The mobile drawer can be dismissed by
 * dragging the top handle down.
 */
export const ResponsiveDialog = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  className,
  size = 'default',
  showCloseButton = true,
  mobileSide = 'bottom',
  mobileFullBleed = false,
  repositionInputs = true
}: ResponsiveDialogProps) => {
  const isMobile = useIsMobile();

  // Do NOT early-return when closed. Both Vaul (mobile) and Radix (desktop)
  // drive their enter/exit animations from the `open` prop and keep the
  // content mounted (via Presence) until the close animation finishes.
  // Returning null here unmounts the drawer mid-slide, so it jumps shut
  // halfway instead of animating closed. The portal content is only mounted
  // while open/animating, so always rendering the root costs nothing.

  if (isMobile) {
    return (
      <VaulDrawer.Root
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        direction={mobileSide}
        repositionInputs={repositionInputs}
      >
        <VaulDrawer.Portal>
          <VaulDrawer.Overlay className="fixed inset-0 z-[110] bg-zinc-900/40" />
          <VaulDrawer.Content
            aria-describedby={undefined}
            className={cn(
              'fixed z-[120] flex flex-col gap-2 bg-white p-6 pt-2 shadow-lg outline-none',
              MOBILE_DIRECTION_CLASSES[mobileSide],
              mobileFullBleed && 'px-0 pb-0',
              className
            )}
          >
            {/* Drag handle. Vaul makes this the primary drag target for closing. */}
            {mobileSide === 'bottom' && (
              <VaulDrawer.Handle className="!mx-auto !mt-2 !mb-2 !h-1 !w-12 !rounded-xl !bg-border" />
            )}
            <div className={cn('flex flex-col space-y-2 text-left', mobileFullBleed && 'px-6')}>
              {title ? (
                <VaulDrawer.Title className="text-lg font-semibold text-gray-900">{title}</VaulDrawer.Title>
              ) : (
                <VaulDrawer.Title className="sr-only">Dialog</VaulDrawer.Title>
              )}
              {description ? (
                <VaulDrawer.Description className="text-sm text-gray-500">{description}</VaulDrawer.Description>
              ) : (
                <VaulDrawer.Description className="sr-only">Dialog content</VaulDrawer.Description>
              )}
            </div>
            {children}
          </VaulDrawer.Content>
        </VaulDrawer.Portal>
      </VaulDrawer.Root>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      className={className}
      size={size}
      showCloseButton={showCloseButton}
    >
      {children}
    </Dialog>
  );
};
